// Private dashboard: conversion funnel from CTA click -> form start -> lead,
// with drop-off broken down by placement and device.
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  Cell,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const ADMIN_TOKEN_KEY = "cta-clicks:admin-token:v1";

function getAdminToken(): string {
  if (typeof window === "undefined") return "";
  let token = window.sessionStorage.getItem(ADMIN_TOKEN_KEY) ?? "";
  if (!token) {
    token = window.prompt("Enter ADMIN_TOKEN to view the funnel") ?? "";
    if (token) window.sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
  }
  return token;
}

interface Segment {
  name: string;
  clicks: number;
  sessions: number;
  starts: number;
  leads: number;
  startRate: number;
  conversionRate: number;
  dropOff: number;
}

interface Payload {
  days: number;
  totals: {
    clicks: number;
    sessions: number;
    anonymousClicks: number;
    formStarts: number;
    leads: number;
    attributedLeads: number;
    directChannelSessions: number;
    startRate: number;
    submitRate: number;
    overallRate: number;
  };
  stages: Array<{
    key: string;
    label: string;
    count: number;
    rateOfPrevious: number;
    dropOff: number;
  }>;
  placements: Segment[];
  devices: Segment[];
  pages: Array<{ name: string; clicks: number; leads: number; conversionRate: number }>;
  daily: Array<{ date: string; engaged: number; starts: number; leads: number }>;
}

export const Route = createFileRoute("/admin/funnel")({
  head: () => ({
    meta: [
      { title: "Conversion Funnel | Arif Khan" },
      { name: "robots", content: "noindex,nofollow" },
      {
        name: "description",
        content: "Private funnel dashboard: CTA clicks to enquiries.",
      },
    ],
  }),
  component: FunnelDashboard,
});

const STAGE_COLORS = ["#0ea5e9", "#a78bfa", "#22c55e"];
const DEVICE_COLORS: Record<string, string> = {
  desktop: "#0ea5e9",
  mobile: "#25D366",
  tablet: "#f59e0b",
  unknown: "#94a3b8",
};

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-ink-soft">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-semibold">{value}</div>
        {hint && <p className="mt-1 text-xs text-ink-soft">{hint}</p>}
      </CardContent>
    </Card>
  );
}

function SegmentTable({ title, rows }: { title: string; rows: Segment[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-ink-soft">
              <th className="py-2 pr-4 font-medium">Name</th>
              <th className="py-2 pr-4 font-medium">Clicks</th>
              <th className="py-2 pr-4 font-medium">Sessions</th>
              <th className="py-2 pr-4 font-medium">Form starts</th>
              <th className="py-2 pr-4 font-medium">Leads</th>
              <th className="py-2 pr-4 font-medium">Conv.</th>
              <th className="py-2 font-medium">Drop-off</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="py-4 text-ink-soft">
                  No data yet for this period.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.name} className="border-t border-border/60">
                <td className="py-2 pr-4">{r.name}</td>
                <td className="py-2 pr-4 tabular-nums">{r.clicks}</td>
                <td className="py-2 pr-4 tabular-nums">{r.sessions}</td>
                <td className="py-2 pr-4 tabular-nums">{r.starts}</td>
                <td className="py-2 pr-4 tabular-nums">{r.leads}</td>
                <td className="py-2 pr-4 tabular-nums">{r.conversionRate}%</td>
                <td className="py-2 tabular-nums">
                  <span
                    className={
                      r.dropOff >= 90
                        ? "text-red-500"
                        : r.dropOff >= 70
                          ? "text-amber-500"
                          : "text-emerald-500"
                    }
                  >
                    {r.dropOff}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function FunnelDashboard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<Payload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (d: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/funnel?days=${d}`, {
        headers: { "x-admin-token": getAdminToken() },
        cache: "no-store",
      });
      if (!res.ok) {
        if (res.status === 401) window.sessionStorage.removeItem(ADMIN_TOKEN_KEY);
        throw new Error(`Request failed (${res.status})`);
      }
      setData((await res.json()) as Payload);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(days);
  }, [days, load]);

  return (
    <main className="min-h-screen bg-background px-6 py-16">
      <div className="mx-auto max-w-6xl space-y-8">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">
              Conversion Funnel
            </h1>
            <p className="mt-2 text-sm text-ink-soft">
              How CTA clicks turn into enquiries — and where visitors drop off,
              by placement and device.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {[7, 30, 90].map((d) => (
              <Button
                key={d}
                variant={d === days ? "default" : "outline"}
                size="sm"
                onClick={() => setDays(d)}
              >
                {d}d
              </Button>
            ))}
            <Button size="sm" variant="ghost" onClick={() => void load(days)}>
              Refresh
            </Button>
          </div>
        </header>

        {error && (
          <p className="text-sm text-red-500" role="alert">
            {error}
          </p>
        )}
        {loading && !data && <p className="text-sm text-ink-soft">Loading…</p>}

        {data && (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Stat
                label="Engaged sessions"
                value={String(data.totals.sessions)}
                hint={`${data.totals.clicks} CTA clicks total`}
              />
              <Stat
                label="Started the form"
                value={String(data.totals.formStarts)}
                hint={`${data.totals.startRate}% of engaged sessions`}
              />
              <Stat
                label="Enquiries"
                value={String(data.totals.leads)}
                hint={`${data.totals.submitRate}% of form starts complete`}
              />
              <Stat
                label="Click → enquiry"
                value={`${data.totals.overallRate}%`}
                hint={`${data.totals.directChannelSessions} sessions used phone/WhatsApp/email instead`}
              />
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Funnel stages</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.stages} layout="vertical" margin={{ left: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis type="number" allowDecimals={false} fontSize={12} />
                      <YAxis
                        type="category"
                        dataKey="label"
                        width={140}
                        fontSize={12}
                      />
                      <Tooltip />
                      <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                        {data.stages.map((s, i) => (
                          <Cell key={s.key} fill={STAGE_COLORS[i % STAGE_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  {data.stages.map((s, i) => (
                    <div
                      key={s.key}
                      className="rounded-lg border border-border/60 p-4"
                    >
                      <p className="text-xs uppercase tracking-wide text-ink-soft">
                        {s.label}
                      </p>
                      <p className="mt-1 text-2xl font-semibold tabular-nums">
                        {s.count}
                      </p>
                      <p className="mt-1 text-xs text-ink-soft">
                        {i === 0
                          ? "Entry stage"
                          : `${s.rateOfPrevious}% continued · ${s.dropOff} dropped off`}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Funnel over time</CardTitle>
              </CardHeader>
              <CardContent className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.daily}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="date" fontSize={12} />
                    <YAxis allowDecimals={false} fontSize={12} />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="engaged"
                      name="CTA clicks"
                      stroke="#0ea5e9"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="starts"
                      name="Form starts"
                      stroke="#a78bfa"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="leads"
                      name="Enquiries"
                      stroke="#22c55e"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Drop-off by device</CardTitle>
              </CardHeader>
              <CardContent className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.devices}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="name" fontSize={12} />
                    <YAxis allowDecimals={false} fontSize={12} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="sessions" name="Sessions" radius={[6, 6, 0, 0]}>
                      {data.devices.map((d) => (
                        <Cell
                          key={d.name}
                          fill={DEVICE_COLORS[d.name] ?? "#94a3b8"}
                        />
                      ))}
                    </Bar>
                    <Bar
                      dataKey="starts"
                      name="Form starts"
                      fill="#a78bfa"
                      radius={[6, 6, 0, 0]}
                    />
                    <Bar
                      dataKey="leads"
                      name="Enquiries"
                      fill="#22c55e"
                      radius={[6, 6, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <SegmentTable title="Drop-off by device" rows={data.devices} />
            <SegmentTable title="Drop-off by placement" rows={data.placements} />

            <Card>
              <CardHeader>
                <CardTitle>Top pages</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-ink-soft">
                      <th className="py-2 pr-4 font-medium">Page</th>
                      <th className="py-2 pr-4 font-medium">CTA clicks</th>
                      <th className="py-2 pr-4 font-medium">Enquiries</th>
                      <th className="py-2 font-medium">Conv.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.pages.map((p) => (
                      <tr key={p.name} className="border-t border-border/60">
                        <td className="py-2 pr-4">{p.name}</td>
                        <td className="py-2 pr-4 tabular-nums">{p.clicks}</td>
                        <td className="py-2 pr-4 tabular-nums">{p.leads}</td>
                        <td className="py-2 tabular-nums">{p.conversionRate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <p className="text-xs text-ink-soft">
              Sessions are anonymous, tab-scoped identifiers used only to link a
              click to a later submission. Known crawlers are excluded.
              {data.totals.anonymousClicks > 0 &&
                ` ${data.totals.anonymousClicks} clicks arrived without a session id (older data or storage blocked) and count toward click totals only.`}
            </p>
          </>
        )}
      </div>
    </main>
  );
}
